import React from 'react'
import {
  useInspectorContext,
  useUserColorFormat,
  ElementType,
  isLayerWithSharedStyle,
} from '../../hooks/inspector'
import {
  isArtboardElement,
  SketchArtboardElement,
  SketchLayerElement,
  SketchElement,
  isPageElement,
} from '../../hooks/inspector/sketchSceneLib'

import {
  PositionAndSize,
  Overview,
  InspectorSkeleton,
  LayoutSettings,
  Exports,
  GridSettings,
  ArtboardOverview,
  Corners,
  Appearance,
  Text,
  TextContent,
  Style,
  TextOrLayerStyles,
  SymbolInSidebar,
} from './sections'
import { Box, Flex } from '@sketch/components'

import * as S from './ArtboardDetailInspector.styles'
import {
  useEditableComponentDescription,
  useSetupComponentDescriptions,
} from './useEditableComponentDescription'
import { EditableComponentDescription } from 'modules/shares/ComponentsWebView/components/ComponentDescription'
import { ShareWithoutVersion } from 'modules/versioning'
import { ArtboardDetailInfoFragment, VersionFragment } from '@sketch/gql-types'
import { useComponentsTrigger } from 'modules/shares/components/ComponentsStateContext'
import { useShowIntroductionModal } from 'modules/shares/ArtboardDetailView/components/ArtboardDetailInspectorIntroductionModal'

type ArtboardDetailInspectorProps = {
  share: ShareWithoutVersion
  artboard: ArtboardDetailInfoFragment
  currentVersion: VersionFragment | undefined
}

export function ArtboardDetailInspector({
  share,
  artboard,
  currentVersion,
}: ArtboardDetailInspectorProps) {
  const {
    selectedElement,
    sketchSceneRootElement,
    isInspectorReady,
  } = useInspectorContext()

  // Start fetching before isInspectorReady is true
  useSetupComponentDescriptions(share.identifier, artboard, currentVersion)

  // Trigger async components ingestion
  useComponentsTrigger()

  // Show the introduction modal if not seen yet
  useShowIntroductionModal()

  const inspectedElement = selectedElement
    ? selectedElement
    : sketchSceneRootElement

  if (!isInspectorReady) {
    return <InspectorSkeleton />
  }

  if (!inspectedElement) {
    return null
  }

  if (isPageElement(inspectedElement)) {
    throw new Error('Page element not yet supported in inspector')
  }

  return (
    <S.Container>
      {isArtboardElement(inspectedElement) ? (
        <ArtboardElementInfo
          artboardElement={inspectedElement}
          currentVersion={currentVersion}
        />
      ) : (
        <LayerElementInfo
          currentVersion={currentVersion}
          layerElement={inspectedElement}
          shareIdentifier={share.identifier}
        />
      )}
    </S.Container>
  )
}

type ArtboardElementInfoProps = {
  artboardElement: SketchArtboardElement
  currentVersion: VersionFragment | undefined
}

function ArtboardElementInfo({
  artboardElement,
  currentVersion,
}: ArtboardElementInfoProps) {
  const [colorFormat, setColorFormat] = useUserColorFormat()
  const { hasAnyLayerWithExportableAssets } = useInspectorContext()

  const {
    description,
    isEnabled,
    editDescriptionHandler,
  } = useEditableComponentDescription()

  return (
    <>
      <Overview sketchElement={artboardElement} />
      {artboardElement.type === ElementType.SymbolMaster && (
        <EditableComponentDescription
          description={description}
          disabled={!isEnabled}
          onEdit={editDescriptionHandler}
        />
      )}
      <ArtboardOverview
        color={artboardElement.backgroundColor}
        preset={artboardElement.preset}
        frame={artboardElement.rootRelativeBoxRect}
        colorFormat={colorFormat}
        onColorFormatChange={setColorFormat}
      />
      <LayoutSettings layout={artboardElement.layoutSettings} />
      <GridSettings grid={artboardElement.gridSettings} />
      {currentVersion && (
        <Box marginTop="auto">
          <Exports
            exportType="artboard"
            exportFormats={artboardElement.exportFormats}
            hasOtherExportableLayers={hasAnyLayerWithExportableAssets}
            currentVersion={currentVersion}
            exportableAssetId={artboardElement.exportId}
          />
        </Box>
      )}
    </>
  )
}

type LayerElementInfoProps = {
  layerElement: SketchLayerElement
  currentVersion: VersionFragment | undefined
  shareIdentifier: string
}
function LayerElementInfo({
  layerElement,
  currentVersion,
  shareIdentifier,
}: LayerElementInfoProps) {
  const { hasAnyLayerWithExportableAssets } = useInspectorContext()
  const [colorFormat, setColorFormat] = useUserColorFormat()

  const isDirty =
    layerElement.style && 'isDirty' in layerElement.style
      ? layerElement.style?.isDirty
      : false

  const textOrLayerStyle = isLayerWithSharedStyle(layerElement.style)
    ? layerElement.style.sharedStyleReference
    : undefined

  const hasSymbols =
    layerElement.type === ElementType.SymbolInstance ||
    !!layerElement.parentSymbolLayerElement

  return (
    <>
      <Flex
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        pr={5}
      >
        <Overview sketchElement={layerElement} />
      </Flex>
      <PositionAndSize
        x={layerElement.rootRelativeBoxRect.x}
        y={layerElement.rootRelativeBoxRect.y}
        width={layerElement.layerSize.width}
        height={layerElement.layerSize.height}
        rotate={layerElement.rotation}
      />

      {/* Text or Layer styles */}
      {textOrLayerStyle && currentVersion && (
        <TextOrLayerStyles
          isDirty={isDirty}
          isTextStyleComponent={layerElement.type === ElementType.Text}
          shareIdentifier={shareIdentifier}
          currentVersion={currentVersion}
          sharedStyleReference={textOrLayerStyle}
        />
      )}

      {layerElement.corners && <Corners corners={layerElement.corners} />}
      {layerElement.appearance && (
        <Appearance appearance={layerElement.appearance} />
      )}
      <TextSection sketchElement={layerElement} />
      {layerElement.style && (
        <Style
          style={layerElement.style}
          onColorFormatChange={setColorFormat}
          colorFormat={colorFormat}
          layerType={layerElement.type}
        />
      )}
      {layerElement.text && (
        <TextContent>{layerElement.text.string}</TextContent>
      )}

      {/* Symbols */}
      {hasSymbols && <SymbolInSidebar layerElement={layerElement} />}

      {currentVersion && (
        <Box marginTop="auto">
          <Exports
            exportType="layer"
            exportFormats={layerElement.exportFormats}
            hasOtherExportableLayers={hasAnyLayerWithExportableAssets}
            currentVersion={currentVersion}
            exportableAssetId={layerElement.exportId}
          />
        </Box>
      )}
    </>
  )
}

type TextSectionProps = {
  sketchElement: SketchElement
}

function TextSection({ sketchElement }: TextSectionProps) {
  const [colorFormat, setColorFormat] = useUserColorFormat()

  if (sketchElement.type !== ElementType.Text || !sketchElement.text) {
    return null
  }

  const style = sketchElement.style

  return (
    <Text
      attributedString={sketchElement.text}
      verticalAlignment={style.verticalAlignment}
      originalSharedStyleValues={style.originalSharedStyleValues}
      onColorFormatChange={setColorFormat}
      colorFormat={colorFormat}
    />
  )
}
